import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GoogleMap, Marker, InfoWindow, Autocomplete } from "@react-google-maps/api";
import { QRCodeCanvas } from "qrcode.react";
import UserService from "../services/user.service";
import TariffComparisonModal from "./TariffComparisonModal";
import { formatLegalVersion } from "../config/legal";

export const TRIAL_STORAGE_KEY = "myn_selfservice_trial";

// Debug-Ausgaben (JSON) nur bei gesetztem Schalter REACT_APP_DEBUG=true.
const IS_DEBUG = process.env.REACT_APP_DEBUG === "true";
const debugLog = (tag, context) => {
  if (!IS_DEBUG) return;
  // eslint-disable-next-line no-console
  console.log(JSON.stringify({ level: "debug", tag: `home.qrstudio.${tag}`, ts: new Date().toISOString(), ...context }));
};

// Fallback-Texte des Hero-Bereichs. Dienstname, Werbetext, Bild und Akzentfarbe
// kommen aus dem im Systemadmin gepflegten Dienst der Startseiten-Demo-Charge.
const HERO_DEFAULTS = {
  serviceName: "QR-Code",
  lead: "Unternehmen suchen, QR-Code mit MYN.CLUB-Wasserzeichen erzeugen, als Bild herunterladen und sofort teilen. Ein Konto brauchen Sie erst, wenn Sie den Code dauerhaft nutzen möchten.",
  accent: "#1b4f9c",
};

// Teilen-Ziele, die im Hero und im Test-Code-Bereich angezeigt werden.
const SHARE_PLATFORMS = [
  { id: "whatsapp", label: "WhatsApp", color: "#25D366" },
  { id: "facebook", label: "Facebook", color: "#1877F2" },
  { id: "instagram", label: "Instagram", color: "#E1306C" },
  { id: "tiktok", label: "TikTok", color: "#111111" },
];

const PERIOD_LABELS = {
  onetime: "einmalig",
  daily: "pro Tag",
  monthly: "pro Monat",
  yearly: "pro Jahr",
};

const DEFAULT_MAP_CENTER = { lat: 50.9375, lng: 6.9603 }; // Köln

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "–";
  return amount.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function placeholderGradient(name) {
  let h = 0;
  const str = String(name || "MYN");
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  const hue1 = h % 360;
  const hue2 = (hue1 + 45) % 360;
  return {
    from: `hsl(${hue1}, 65%, 45%)`,
    to: `hsl(${hue2}, 70%, 30%)`,
  };
}

// Seitenzahlen der Live-Liste: erste, letzte und Nachbarn der aktuellen Seite.
function pagerItems(page, pages) {
  const wanted = new Set([1, pages, page - 1, page, page + 1]);
  const items = [];
  let last = 0;
  for (let p = 1; p <= pages; p++) {
    if (!wanted.has(p)) continue;
    if (p - last > 1) items.push("gap");
    items.push(p);
    last = p;
  }
  return items;
}

function placeholderInitials(name) {
  const src = (String(name || "").trim() || "MYN").replace(/[^\p{L}\p{N}]/gu, "");
  const ch = Array.from(src).slice(0, 2).join("").toUpperCase();
  return ch || "M";
}

const paintWatermark = (canvas) => {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const box = Math.round(canvas.width * 0.22);
  const x = (canvas.width - box) / 2;
  const y = (canvas.height - box) / 2;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(x, y, box, box);
  ctx.strokeStyle = "#1b4f9c";
  ctx.lineWidth = 2;
  ctx.strokeRect(x + 1, y + 1, box - 2, box - 2);
  ctx.fillStyle = "#1b4f9c";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `bold ${Math.round(box * 0.28)}px sans-serif`;
  ctx.fillText("MYN", x + box / 2, y + box / 2);
};

const WatermarkedQr = ({ value, size = 220 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const timer = window.setTimeout(() => paintWatermark(ref.current), 30);
    return () => window.clearTimeout(timer);
  }, [value, size]);
  if (!value) return null;
  return (
    <QRCodeCanvas
      ref={ref}
      value={value}
      size={size}
      level="H"
      includeMargin
      aria-label="QR-Code mit MYN.CLUB-Wasserzeichen"
    />
  );
};

// Vektor-Icons der Teilen-Ziele (skalieren verlustfrei auf Retina/4K).
const SocialIcon = ({ id, size = 22 }) => {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", focusable: "false" };
  switch (id) {
    case "whatsapp":
      return (
        <svg {...common}>
          <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.3.1-.2 0-.3 0-.5l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.8 12 12 0 0 0 4.6 4c.6.3 1.1.4 1.5.5.6.2 1.2.2 1.7.1.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.5-.3z" />
        </svg>
      );
    case "facebook":
      return (
        <svg {...common}>
          <path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12z" />
        </svg>
      );
    case "instagram":
      return (
        <svg {...common}>
          <path d="M12 7.3A4.7 4.7 0 1 0 12 16.7 4.7 4.7 0 0 0 12 7.3zm0 7.7a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm4.9-7.9a1.1 1.1 0 1 1-2.2 0 1.1 1.1 0 0 1 2.2 0zM12 2.2c-2.7 0-3 0-4 .1a7 7 0 0 0-2.4.4 4.9 4.9 0 0 0-2.8 2.8 7 7 0 0 0-.4 2.4c-.1 1-.1 1.3-.1 4s0 3 .1 4a7 7 0 0 0 .4 2.4 4.9 4.9 0 0 0 2.8 2.8 7 7 0 0 0 2.4.4c1 .1 1.3.1 4 .1s3 0 4-.1a7 7 0 0 0 2.4-.4 4.9 4.9 0 0 0 2.8-2.8 7 7 0 0 0 .4-2.4c.1-1 .1-1.3.1-4s0-3-.1-4a7 7 0 0 0-.4-2.4 4.9 4.9 0 0 0-2.8-2.8 7 7 0 0 0-2.4-.4c-1-.1-1.3-.1-4-.1zm0 1.7c2.7 0 3 0 4 .1.9 0 1.5.2 1.8.3.5.2.8.4 1.1.7.3.3.6.7.7 1.1.1.3.3.9.3 1.8.1 1 .1 1.3.1 4s0 3-.1 4c0 .9-.2 1.5-.3 1.8-.2.5-.4.8-.7 1.1-.3.3-.7.6-1.1.7-.3.1-.9.3-1.8.3-1 .1-1.3.1-4 .1s-3 0-4-.1c-.9 0-1.5-.2-1.8-.3a3.2 3.2 0 0 1-1.1-.7 3.2 3.2 0 0 1-.7-1.1c-.1-.3-.3-.9-.3-1.8-.1-1-.1-1.3-.1-4s0-3 .1-4c0-.9.2-1.5.3-1.8.2-.5.4-.8.7-1.1.3-.3.7-.6 1.1-.7.3-.1.9-.3 1.8-.3 1-.1 1.3-.1 4-.1z" />
        </svg>
      );
    case "tiktok":
      return (
        <svg {...common}>
          <path d="M16.6 5.8A4.3 4.3 0 0 1 15.5 3h-3.1v12.4a2.6 2.6 0 1 1-2.6-2.6c.3 0 .5 0 .8.1V9.7a5.8 5.8 0 1 0 5 5.7V9.1a7.3 7.3 0 0 0 4.3 1.4V7.4a4.3 4.3 0 0 1-3.3-1.6z" />
        </svg>
      );
    case "download":
      return (
        <svg {...common}>
          <path d="M12 3a1 1 0 0 1 1 1v9.6l2.3-2.3a1 1 0 1 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.4L11 13.6V4a1 1 0 0 1 1-1zM5 17a1 1 0 0 1 1 1v1h12v-1a1 1 0 1 1 2 0v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1z" />
        </svg>
      );
    case "share":
      return (
        <svg {...common}>
          <path d="M18 16a3 3 0 0 0-2.4 1.2l-7-3.6a3 3 0 0 0 0-1.2l7-3.6A3 3 0 1 0 15 7a3 3 0 0 0 .1.7l-7 3.6a3 3 0 1 0 0 3.4l7 3.6A3 3 0 1 0 18 16z" />
        </svg>
      );
    default:
      return null;
  }
};

const HomeQrStudio = ({ isLoaded, hostName }) => {
  const navigate = useNavigate();
  const autoRef = useRef(null);
  const mapRef = useRef(null);

  const [catalog, setCatalog] = useState({ enabled: false, offers: [] });
  const [batchId, setBatchId] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("yearly");
  const [company, setCompany] = useState(null);
  const [trial, setTrial] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("");
  const [liveEntries, setLiveEntries] = useState([]);
  const [liveSearchQuery, setLiveSearchQuery] = useState("");
  const [livePaging, setLivePaging] = useState({ page: 1, pages: 1, total: 0 });
  // Refs, damit die automatische Aktualisierung stets Seite und Suche kennt.
  const livePageRef = useRef(1);
  const liveSearchRef = useRef("");
  const liveRequestRef = useRef(0);
  const [copyFeedback, setCopyFeedback] = useState("");
  // Zustimmung zu AGB/Datenschutz vor dem Erzeugen des kostenlosen Test-Codes.
  const [consentAccepted, setConsentAccepted] = useState(false);
  // Kann dieses Geraet Dateien (PNG) direkt an Apps wie WhatsApp uebergeben?
  const [canShareFiles, setCanShareFiles] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);

  useEffect(() => {
    try {
      if (typeof navigator !== "undefined" && typeof navigator.canShare === "function" && typeof File === "function") {
        const probe = new File([new Uint8Array([137, 80, 78, 71])], "probe.png", { type: "image/png" });
        setCanShareFiles(!!navigator.canShare({ files: [probe] }));
      }
    } catch (_) {
      setCanShareFiles(false);
    }
  }, []);

  // Karten-Status
  const [mapCenter, setMapCenter] = useState(DEFAULT_MAP_CENTER);
  const [mapZoom, setMapZoom] = useState(13);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);

  useEffect(() => {
    let active = true;
    UserService.getSelfServiceCatalog()
      .then((response) => {
        if (!active) return;
        const data = response.data || { enabled: false, offers: [] };
        setCatalog(data);
        const offers = data.offers || [];
        const demo = offers.find((offer) => offer.is_homepage_demo && offer.available > 0);
        const first = offers.find((offer) => offer.available > 0) || offers[0];
        setBatchId((demo || first || {}).batch_id || null);
      })
      .catch(() => {
        if (active) setCatalog({ enabled: false, offers: [] });
      });
    return () => { active = false; };
  }, []);

  const loadLive = () => {
    const requestedPage = livePageRef.current;
    const requestNo = ++liveRequestRef.current;
    UserService.getSelfServiceLive({ page: requestedPage, search: liveSearchRef.current || undefined })
      .then((response) => {
        if (requestNo !== liveRequestRef.current) return;
        const data = (response && response.data) || {};
        const raw = Array.isArray(data.entries) ? data.entries : [];
        const pages = Math.max(1, Number(data.pages) || 1);
        const page = Math.min(pages, Math.max(1, Number(data.page) || requestedPage));
        livePageRef.current = page;
        setLivePaging({ page, pages, total: Number(data.total) || raw.length });
        debugLog("live.loaded", { page, pages, count: raw.length });
        // Strikte Eindeutigkeit: Ein Unternehmen darf nur genau einmal vorkommen
        const unique = [];
        const seen = new Set();
        raw.forEach((entry) => {
          const key = String(entry.place_id || "").trim() || String(entry.company_name || "").trim().toLowerCase();
          const nameKey = String(entry.company_name || "").trim().toLowerCase();
          if (!seen.has(key) && !seen.has(nameKey)) {
            seen.add(key);
            seen.add(nameKey);
            unique.push(entry);
          }
        });
        setLiveEntries(unique);
      })
      .catch((error) => {
        // Bei Netzfehlern bleiben die zuletzt geladenen Eintraege sichtbar.
        debugLog("live.failed", { status: error && error.response ? error.response.status : null });
      });
  };

  useEffect(() => {
    loadLive();
    const timer = window.setInterval(loadLive, 20000);
    return () => window.clearInterval(timer);
  }, []);

  // Suche wirkt serverseitig ueber alle Seiten; kurze Verzoegerung beim Tippen.
  useEffect(() => {
    const term = liveSearchQuery.trim();
    if (term === liveSearchRef.current) return undefined;
    const timer = window.setTimeout(() => {
      liveSearchRef.current = term;
      livePageRef.current = 1;
      loadLive();
    }, 350);
    return () => window.clearTimeout(timer);
  }, [liveSearchQuery]);

  const goToLivePage = (page) => {
    const target = Math.min(livePaging.pages, Math.max(1, page));
    if (target === livePaging.page) return;
    livePageRef.current = target;
    loadLive();
    const anchor = document.getElementById("gerade-erzeugt");
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (anchor) anchor.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  };

  // Geolocation für die Karte
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const userPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setMapCenter(userPos);
        },
        () => {}
      );
    }
  }, []);

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      setHint("Standortabfrage wird von diesem Browser nicht unterstützt.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const userPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMapCenter(userPos);
        setMapZoom(14);
        if (mapRef.current) {
          mapRef.current.panTo(userPos);
          mapRef.current.setZoom(14);
        }
      },
      () => {
        setHint("Ihr Standort konnte nicht ermittelt werden.");
      }
    );
  };

  const onPlaceChanged = () => {
    const place = autoRef.current && autoRef.current.getPlace ? autoRef.current.getPlace() : null;
    if (!place || !place.geometry || !place.geometry.location || !place.name) {
      setHint("Bitte einen Eintrag aus der Vorschlagsliste wählen.");
      setCompany(null);
      return;
    }
    const coords = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    setHint("");
    setCompany({
      place_id: place.place_id,
      place_name: place.name,
      place_address: place.formatted_address || place.name,
      latitude: coords.lat,
      longitude: coords.lng,
    });
    setMapCenter(coords);
    setMapZoom(15);
    setShowInfoWindow(true);
    if (mapRef.current) {
      mapRef.current.panTo(coords);
      mapRef.current.setZoom(15);
    }
  };

  const createQr = async () => {
    if (!company) {
      setHint("Bitte zuerst ein Unternehmen auf der Karte suchen.");
      return;
    }
    if (!consentAccepted) {
      setHint("Bitte bestätigen Sie zuerst die AGB und die Datenschutzerklärung.");
      return;
    }
    const targetBatchId = batchId || (offers[0] && offers[0].batch_id);
    if (!targetBatchId) {
      setHint("Kein Angebot verfügbar.");
      return;
    }
    setBusy(true);
    setHint("");
    debugLog("trial.request", { batchId: targetBatchId, consentAccepted: true });
    try {
      const response = await UserService.createSelfServiceTrial({
        batch_id: targetBatchId,
        place_name: company.place_name,
        place_address: company.place_address,
        latitude: company.latitude,
        longitude: company.longitude,
        place_id: company.place_id,
        consent_accepted: true,
      });
      const data = response.data || {};
      const stored = {
        trial_token: data.trial_token,
        batch_id: data.batch_id,
        billing_period: billingPeriod,
        company_name: data.company_name,
        company_address: data.company_address,
        redeem_url: data.redeem_url,
      };
      window.sessionStorage.setItem(TRIAL_STORAGE_KEY, JSON.stringify(stored));
      setTrial(data);
      debugLog("trial.created", { batchId: data.batch_id, hasRedeemUrl: !!data.redeem_url });
      livePageRef.current = 1;
      setLiveSearchQuery("");
      liveSearchRef.current = "";
      loadLive();
      // Kurze Verzögerung für garantiertes DOM-Update vor dem Hinscrollen
      window.setTimeout(() => {
        loadLive();
        const liveAnchor = document.getElementById("gerade-erzeugt");
        if (liveAnchor) {
          liveAnchor.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
    } catch (error) {
      const data = error && error.response ? error.response.data : null;
      debugLog("trial.failed", { status: error && error.response ? error.response.status : null, code: data && data.code });
      if (data && data.code === "consent_required") setConsentAccepted(false);
      setHint((data && data.message) || "Der QR-Code konnte nicht erzeugt werden.");
    } finally {
      setBusy(false);
    }
  };

  const qrFileName = () =>
    `myn-club-${(company && company.place_name ? company.place_name : "qr-code").toLowerCase().replace(/[^a-z0-9]/g, "-")}.png`;

  const getQrCanvas = () => document.querySelector(".myn-studio__qr canvas");

  const downloadQr = () => {
    const canvas = getQrCanvas();
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = qrFileName();
    link.click();
    debugLog("qr.download", { fileName: link.download });
  };

  const copyShareLink = (platform) => {
    if (!trial || !trial.redeem_url) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(trial.redeem_url);
      setCopyFeedback(`Link für ${platform} in die Zwischenablage kopiert!`);
      window.setTimeout(() => setCopyFeedback(""), 3500);
    }
  };

  // Liefert das Wasserzeichen-Bild als PNG-Datei (fuer die Web-Share-API).
  const buildQrFile = () =>
    new Promise((resolve) => {
      const canvas = getQrCanvas();
      if (!canvas || typeof canvas.toBlob !== "function") return resolve(null);
      canvas.toBlob((blob) => {
        if (!blob) return resolve(null);
        resolve(new File([blob], qrFileName(), { type: "image/png" }));
      }, "image/png");
    });

  const shareText = () =>
    trial ? `${trial.company_name} – ${serviceLabel}: ${trial.redeem_url}` : "";

  // Intelligentes WhatsApp-Teilen:
  // Am Computer (Mac/PC) wird der QR-Code als PNG heruntergeladen und WhatsApp Web in einem
  // neuen Tab geöffnet (verhindert das Öffnen des macOS-Share-Sheets, in dem WhatsApp fehlt).
  // Auf Mobilgeräten wird direkt der WhatsApp-Link aufgerufen.
  const handleShareWhatsApp = () => {
    if (!trial || !trial.redeem_url) return;
    const isMobile =
      typeof navigator !== "undefined" &&
      /Android|iPhone|iPad|iPod|webOS/i.test(navigator.userAgent || "");
    const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText())}`;

    if (!isMobile) {
      downloadQr();
      window.open(waUrl, "_blank", "noopener,noreferrer");
      setCopyFeedback(
        "WhatsApp Web geöffnet & QR-Code heruntergeladen! Fügen Sie das Bild einfach in Ihren Chat ein."
      );
      window.setTimeout(() => setCopyFeedback(""), 6000);
      debugLog("qr.share.whatsapp.desktop", { ok: true });
    } else {
      window.open(waUrl, "_blank", "noopener,noreferrer");
      setCopyFeedback("WhatsApp wird geöffnet …");
      window.setTimeout(() => setCopyFeedback(""), 3500);
      debugLog("qr.share.whatsapp.mobile", { ok: true });
    }
  };

  // Teilt den QR-Code als Bild direkt an WhatsApp, Instagram, TikTok usw.
  // (Smartphone/Tablet). Faellt auf Download + Link-Kopie zurueck, wenn das
  // Geraet keine Dateien teilen kann (z. B. Desktop-Browser).
  const shareQrImage = async (platform) => {
    if (!trial || !trial.redeem_url || shareBusy) return;
    setShareBusy(true);
    try {
      const file = await buildQrFile();
      if (file && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: `${trial.company_name} – ${serviceLabel}`, text: shareText() });
          setCopyFeedback("QR-Code als Bild übergeben. Wählen Sie jetzt die gewünschte App.");
          debugLog("qr.share.files", { platform, ok: true });
        } catch (error) {
          const aborted = error && error.name === "AbortError";
          debugLog("qr.share.files", { platform, ok: false, aborted });
          if (!aborted) {
            downloadQr();
            copyShareLink(platform);
          }
        }
      } else {
        downloadQr();
        copyShareLink(platform);
        setCopyFeedback(`Bild heruntergeladen und Link kopiert. Fügen Sie beides in ${platform} ein.`);
        debugLog("qr.share.fallback", { platform });
      }
      window.setTimeout(() => setCopyFeedback(""), 4500);
    } finally {
      setShareBusy(false);
    }
  };

  // Scrollt zum Suchfeld auf der Karte und setzt den Fokus (Hero-CTA).
  const focusSearch = () => {
    const input = document.getElementById("pac-input");
    const anchor = document.getElementById("qr-erzeugen");
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (anchor) anchor.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    if (input) window.setTimeout(() => input.focus({ preventScroll: true }), reduce ? 0 : 450);
    debugLog("hero.cta", { target: "pac-input", found: !!input });
  };

  const handleSelectOfferFromModal = (newBatchId, newPeriod) => {
    setBatchId(newBatchId);
    if (newPeriod) setBillingPeriod(newPeriod);
  };

  const offers = catalog.offers || [];
  const selected = offers.find((offer) => String(offer.batch_id) === String(batchId));

  // Dienst der Startseiten-Demo (im Systemadmin gepflegt) steuert Texte,
  // Farbe und Bild des Hero-Bereichs. Kein Dienstname ist fest verdrahtet.
  const heroOffer = offers.find((offer) => offer.is_homepage_demo) || selected || offers[0] || null;
  const heroService = (heroOffer && heroOffer.service) || {};
  const serviceLabel = String(heroService.service_name || "").replace(/\s+/g, " ").trim() || HERO_DEFAULTS.serviceName;
  const heroLead = (heroService.ad_text || heroService.description || "").trim() || HERO_DEFAULTS.lead;
  const heroAccent = /^#[0-9a-f]{3,8}$/i.test(String(heroService.accent_color || "")) ? heroService.accent_color : HERO_DEFAULTS.accent;
  const legalVersion = formatLegalVersion(catalog.legal && catalog.legal.agb_version);
  const privacyVersion = formatLegalVersion(catalog.legal && catalog.legal.privacy_version);
  const demoQrValue = `${window.location.origin}/`;

  // Filterung der Live-Liste über das Suchfeld
  const filteredLiveEntries = liveEntries.filter((entry) => {
    const q = liveSearchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      (entry.company_name && entry.company_name.toLowerCase().includes(q)) ||
      (entry.company_address && entry.company_address.toLowerCase().includes(q)) ||
      (entry.service_name && entry.service_name.toLowerCase().includes(q))
    );
  });

  return (
    <section id="selbst-buchen" className="myn-section myn-selfservice">
      <div className="myn-container">
        {/* HERO: Werbe-Eyecatcher. Texte, Farbe und Bild kommen aus dem Dienst
            der Startseiten-Demo-Charge (Systemadmin), nichts ist fest verdrahtet. */}
        <header className="myn-hero" style={{ "--myn-hero-accent": heroAccent }}>
          <div className="myn-hero__bg" aria-hidden="true" />
          <div className="myn-hero__grid">
            <div className="myn-hero__copy">
              <span className="myn-hero__kicker">
                {heroService.icon_url ? (
                  <img src={heroService.icon_url} alt="" className="myn-hero__kicker-icon" loading="lazy" />
                ) : (
                  <span className="myn-hero__kicker-dot" aria-hidden="true" />
                )}
                {serviceLabel} · Kostenlos testen
              </span>

              <h1 className="myn-hero__title">
                Ihr {serviceLabel}-QR-Code.
                <span className="myn-hero__title-accent">Kostenlos. Sofort. Ohne Anmeldung.</span>
              </h1>

              <p className="myn-hero__lead">{heroLead}</p>

              <ul className="myn-hero__badges" aria-label="Ihre Vorteile">
                <li><strong>0 €</strong> im Test</li>
                <li><strong>Keine</strong> Registrierung</li>
                <li><strong>PNG</strong> zum Download</li>
                <li><strong>Teilen</strong> als Bild oder Link</li>
              </ul>

              <ol className="myn-hero__steps" aria-label="So funktioniert es">
                <li><span className="myn-hero__step-no">1</span><span>Unternehmen suchen</span></li>
                <li><span className="myn-hero__step-no">2</span><span>QR-Code erzeugen</span></li>
                <li><span className="myn-hero__step-no">3</span><span>Herunterladen &amp; teilen</span></li>
              </ol>

              <div className="myn-hero__cta">
                <button type="button" className="myn-btn myn-btn--primary myn-hero__cta-main" onClick={focusSearch}>
                  Jetzt kostenlos QR-Code erzeugen
                </button>
                <a href="#tarife-waehlen" className="myn-btn myn-btn--ghost myn-hero__cta-sub">
                  Tarife ansehen
                </a>
              </div>

              <div className="myn-hero__share">
                <span className="myn-hero__share-label">Direkt teilen per</span>
                <ul className="myn-hero__share-list" aria-label="Teilen-Ziele">
                  {SHARE_PLATFORMS.map((platform) => (
                    <li key={platform.id} style={{ "--myn-share-color": platform.color }}>
                      <SocialIcon id={platform.id} size={20} />
                      <span>{platform.label}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="myn-hero__trust">
                {hostName && <span>Vertragspartner: {hostName}</span>}
                <span>Gespeichert werden nur öffentliche Unternehmensangaben.</span>
                <span>
                  <Link to="/agb">AGB</Link> (Stand {legalVersion}) · <Link to="/datenschutz">Datenschutz</Link> (Stand {privacyVersion})
                </span>
              </p>
            </div>

            <div className="myn-hero__visual" aria-hidden="true">
              <div className="myn-hero__phone">
                <span className="myn-hero__phone-notch" />
                <div className="myn-hero__phone-screen">
                  {heroService.ad_image_url ? (
                    <img src={heroService.ad_image_url} alt="" className="myn-hero__phone-img" loading="lazy" />
                  ) : null}
                  <span className="myn-hero__phone-label">{serviceLabel}</span>
                  <div className="myn-hero__phone-qr">
                    <WatermarkedQr value={demoQrValue} size={150} />
                  </div>
                  <span className="myn-hero__phone-foot">Scannen oder klicken – fertig.</span>
                </div>
              </div>
              <span className="myn-hero__float myn-hero__float--1">✓ 0 € · kein Konto</span>
              <span className="myn-hero__float myn-hero__float--2"><SocialIcon id="download" size={18} /> PNG-Download</span>
              <span className="myn-hero__float myn-hero__float--3"><SocialIcon id="whatsapp" size={18} /> Als Bild teilen</span>
            </div>
          </div>
        </header>

        {/* NEUE REIHENFOLGE: SCHRITT 1 & 2 GANZ OBEN (Landkarte & Test-Code) */}
        <div id="qr-erzeugen" className="myn-card myn-studio__work mb-4">
          {/* Linke Spalte: 1. Unternehmen suchen */}
          <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h2 className="myn-h3 mb-0">1. Unternehmen suchen &amp; QR-Code erzeugen</h2>
            </div>
            <p className="text-muted small mb-3">
              Geben Sie Ihr Unternehmen in das Suchfeld auf der Karte ein oder nutzen Sie Ihren aktuellen Standort.
            </p>

            <div className="myn-studio__map-container position-relative">
              {/* Suchleiste auf der Karte */}
              {isLoaded ? (
                <div className="myn-map-search-overlay">
                  <Autocomplete
                    onLoad={(autocomplete) => {
                      autocomplete.setFields(["name", "formatted_address", "geometry", "place_id"]);
                      autoRef.current = autocomplete;
                    }}
                    onPlaceChanged={onPlaceChanged}
                    options={{ types: ["establishment"] }}
                  >
                    <div className="myn-map-search-box">
                      <span className="myn-map-search-icon" aria-hidden="true">🔍</span>
                      <input
                        id="pac-input"
                        className="myn-map-search-input"
                        type="text"
                        placeholder="Unternehmen und Standort eingeben"
                        aria-label="Unternehmen und Standort eingeben"
                        autoComplete="off"
                      />
                    </div>
                  </Autocomplete>
                </div>
              ) : null}

              {/* Google Map */}
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "380px", borderRadius: "14px" }}
                  center={mapCenter}
                  zoom={mapZoom}
                  onLoad={(map) => { mapRef.current = map; }}
                  options={{
                    disableDefaultUI: false,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    zoomControl: true,
                    gestureHandling: "cooperative",
                  }}
                >
                  {company && (
                    <Marker
                      position={{ lat: company.latitude, lng: company.longitude }}
                      onClick={() => setShowInfoWindow(true)}
                    />
                  )}
                  {company && showInfoWindow && (
                    <InfoWindow
                      position={{ lat: company.latitude, lng: company.longitude }}
                      onCloseClick={() => setShowInfoWindow(false)}
                    >
                      <div className="p-1">
                        <strong>{company.place_name}</strong>
                        <br />
                        <span className="small text-muted">{company.place_address}</span>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              ) : (
                <div className="d-flex align-items-center justify-content-center bg-light" style={{ height: "380px", borderRadius: "14px" }}>
                  <p className="text-muted">Karte wird geladen …</p>
                </div>
              )}

              {/* Geolocation Button ("Mein Standort") */}
              {isLoaded && (
                <button
                  type="button"
                  className="myn-map-locate-btn"
                  onClick={handleCurrentLocation}
                  title="Mein aktueller Standort"
                  aria-label="Mein aktueller Standort"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#444">
                    <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0 0 13 3.06V1h-2v2.06A8.994 8.994 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06A8.994 8.994 0 0 0 20.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
                  </svg>
                </button>
              )}
            </div>

            {/* Ausgewähltes Unternehmen & Erstellen-Button */}
            {company ? (
              <div className="myn-studio__picked-box mt-3 p-3 bg-light rounded border">
                <div className="mb-2">
                  <span className="badge bg-success-subtle text-success mb-1">Gefundenes Unternehmen</span>
                  <div className="fw-bold text-dark">{company.place_name}</div>
                  <div className="small text-muted">{company.place_address}</div>
                </div>

                {/* Pflicht-Zustimmung vor dem kostenlosen Test-Code */}
                <div className="myn-consent">
                  <input
                    id="selfservice-trial-consent"
                    type="checkbox"
                    className="myn-consent__input"
                    checked={consentAccepted}
                    onChange={(e) => setConsentAccepted(e.target.checked)}
                    aria-describedby="selfservice-trial-consent-text"
                  />
                  <label htmlFor="selfservice-trial-consent" className="myn-consent__label" id="selfservice-trial-consent-text">
                    Ich darf für dieses Unternehmen handeln und akzeptiere die{" "}
                    <Link to="/agb" target="_blank" rel="noopener noreferrer">AGB</Link> (Stand {legalVersion}) sowie die{" "}
                    <Link to="/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</Link>{" "}
                    (Stand {privacyVersion}). Der Test-Code ist <strong>kostenlos</strong> und erfordert{" "}
                    <strong>keine Registrierung</strong>. Mir ist bekannt, dass Firmenname, Adresse und ein Kartenbild{" "}
                    (Website-Vorschau oder – ohne Website – ein KI-generiertes Bild){" "}
                    <strong>öffentlich</strong> im Bereich „Gerade erzeugt“ auf dieser Seite angezeigt werden – auch nach Ablauf{" "}
                    des Tests, bis ich widerspreche.
                  </label>
                </div>

                <button
                  type="button"
                  className="myn-btn myn-btn--primary myn-studio__create w-100"
                  onClick={createQr}
                  disabled={busy || !consentAccepted}
                  aria-disabled={busy || !consentAccepted}
                  title={consentAccepted ? "Kostenlosen Test-Code erzeugen" : "Bitte zuerst AGB und Datenschutz bestätigen"}
                >
                  {busy ? "QR-Code wird erzeugt …" : "QR-Code jetzt kostenlos testen"}
                </button>
              </div>
            ) : (
              <p className="text-muted small mt-2">
                Tippen Sie oben in das Suchfeld auf der Karte, um Ihr Google-Unternehmensprofil auszuwählen.
              </p>
            )}

            {hint && <p className="myn-studio__hint mt-2 small">{hint}</p>}
          </div>

          {/* Rechte Spalte: 2. Ihr Test-Code & Teilen */}
          <div className="myn-studio__qr d-flex flex-column align-items-center justify-content-center text-center p-3 border rounded bg-white">
            <h2 className="myn-h3 mb-2">2. Ihr Test-Code</h2>
            {trial && trial.redeem_url ? (
              <>
                <div className="p-2 border rounded shadow-sm bg-white mb-2">
                  <WatermarkedQr value={trial.redeem_url} size={190} />
                </div>
                <div className="mb-2">
                  <strong className="d-block">{trial.company_name}</strong>
                  <span className="small text-muted">{trial.company_address}</span>
                </div>
                <p className="small text-muted mb-3">
                  Scannen Sie den QR-Code mit dem Smartphone, laden Sie ihn als Bild herunter oder teilen Sie ihn direkt:
                </p>

                {/* Social Share & Download Buttons */}
                <div className="myn-share-bar mb-2 d-flex flex-wrap justify-content-center gap-2">
                  <button type="button" className="myn-btn myn-btn--ghost myn-share-btn" onClick={downloadQr} title="QR-Code als PNG-Bild speichern">
                    <SocialIcon id="download" size={18} /> Download
                  </button>
                  {canShareFiles && (
                    <button
                      type="button"
                      className="myn-btn myn-btn--primary myn-share-btn"
                      onClick={() => shareQrImage("Ihre App")}
                      disabled={shareBusy}
                      title="QR-Code als Bild an WhatsApp, Instagram, TikTok und weitere Apps übergeben"
                    >
                      <SocialIcon id="share" size={18} /> {shareBusy ? "Wird vorbereitet …" : "Als Bild teilen"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="myn-share-btn myn-share-btn--whatsapp"
                    onClick={handleShareWhatsApp}
                    title="Per WhatsApp teilen"
                  >
                    <SocialIcon id="whatsapp" size={18} /> WhatsApp
                  </button>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(trial.redeem_url)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="myn-share-btn myn-share-btn--facebook"
                    title="Auf Facebook teilen"
                  >
                    <SocialIcon id="facebook" size={18} /> Facebook
                  </a>
                  <button
                    type="button"
                    className="myn-share-btn myn-share-btn--instagram"
                    onClick={() => (canShareFiles ? shareQrImage("Instagram") : copyShareLink("Instagram"))}
                    disabled={shareBusy}
                    title={canShareFiles ? "QR-Code als Bild an Instagram übergeben" : "Link für Instagram kopieren"}
                  >
                    <SocialIcon id="instagram" size={18} /> Instagram
                  </button>
                  <button
                    type="button"
                    className="myn-share-btn myn-share-btn--tiktok"
                    onClick={() => (canShareFiles ? shareQrImage("TikTok") : copyShareLink("TikTok"))}
                    disabled={shareBusy}
                    title={canShareFiles ? "QR-Code als Bild an TikTok übergeben" : "Link für TikTok kopieren"}
                  >
                    <SocialIcon id="tiktok" size={18} /> TikTok
                  </button>
                </div>
                <p className="small text-muted mb-3">
                  Tipp: Beim Klick auf WhatsApp am Computer wird der QR-Code automatisch als Bild heruntergeladen und WhatsApp Web geöffnet. Auf dem Smartphone können Sie ihn auch direkt per „Als Bild teilen“ an jede App senden.
                </p>

                {copyFeedback && (
                  <div className="alert alert-success py-1 px-2 small mb-2">{copyFeedback}</div>
                )}

                <div className="mt-2 pt-2 border-top w-100">
                  <span className="small text-muted d-block mb-2">
                    Möchten Sie diesen Code dauerhaft nutzen?
                  </span>
                  <a href="#tarife-waehlen" className="myn-btn myn-btn--primary w-100 text-center">
                    Tarif wählen &amp; Konto anlegen ↓
                  </a>
                </div>
              </>
            ) : (
              <div className="py-5 text-muted">
                <div style={{ fontSize: "3.5rem" }}>📱</div>
                <p className="mt-2 mb-0">
                  Sobald Sie links ein Unternehmen gewählt haben, erscheint hier Ihr interaktiver Test-QR-Code
                  mit Download und direkten Sharing-Funktionen.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* NEUE REIHENFOLGE: SCHRITT 3 DARUNTER (Angebote wählen & Tarifvergleich) */}
        <div id="tarife-waehlen" className="myn-card mb-4 p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            <div>
              <h2 className="myn-h3 mb-1">3. Angebot &amp; Tarif wählen</h2>
              <p className="text-muted small mb-0">
                Wählen Sie Ihren gewünschten Tarif aus allen verfügbaren Angeboten für Endkunden.
              </p>
            </div>
            {offers.length > 0 && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm rounded-pill px-3 shadow-sm"
                onClick={() => setShowCompareModal(true)}
              >
                🔍 Ausführlicher Tarifvergleich
              </button>
            )}
          </div>

          {/* Horizontale Scrollleiste für Angebote */}
          <div className="myn-studio__offers-scroll-wrapper mb-3">
            <div className="myn-studio__offers-scroll">
              {offers.map((offer) => {
                const isSelected = String(offer.batch_id) === String(batchId);
                const period = PERIOD_LABELS[offer.tariff.amount_period_net] || "";
                const soldOut = Number(offer.available) <= 0;
                const rawMonthly = Number(offer.tariff.amount_eur_net) || 0;
                const discount = Number(offer.tariff.yearly_discount_percent) || 0;
                const discountedMonthly = rawMonthly * (1 - discount / 100);

                return (
                  <div
                    key={offer.batch_id}
                    className={`myn-studio__offer-card${isSelected ? " is-selected" : ""}${soldOut ? " is-disabled" : ""}`}
                    onClick={() => !soldOut && setBatchId(offer.batch_id)}
                    role="button"
                    tabIndex={soldOut ? -1 : 0}
                    aria-pressed={isSelected}
                  >
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <span className="badge bg-primary-subtle text-primary">
                        {offer.service.service_name}
                      </span>
                      {isSelected && (
                        <span className="badge bg-primary text-white">✓ Gewählt</span>
                      )}
                    </div>
                    <strong className="d-block mb-0 text-truncate" title={offer.batch_name}>{offer.batch_name}</strong>
                    <div className="small text-muted mb-1 text-truncate">
                      Tarif: <strong>{offer.tariff.tariff_name}</strong>
                    </div>
                    <div className="my-2">
                      <span className="h5 fw-bold text-dark d-block mb-0">
                        {formatAmount(offer.tariff.amount_eur_net)} €{" "}
                        <span className="text-muted small fw-normal">netto {period}</span>
                      </span>
                      {discount > 0 && offer.tariff.amount_period_net === "monthly" && (
                        <span className="badge bg-success-subtle text-success small mt-1">
                          Nur {formatAmount(discountedMonthly)} € bei Jahreszahlung (-{discount}%)
                        </span>
                      )}
                    </div>
                    <div className="small text-muted mt-2 pt-2 border-top d-flex justify-content-between align-items-center">
                      <span>{soldOut ? "Vergriffen" : `Noch ${offer.available} frei`}</span>
                      <span
                        className="text-primary fw-semibold"
                        onClick={(e) => {
                          e.stopPropagation();
                          setBatchId(offer.batch_id);
                          setShowCompareModal(true);
                        }}
                      >
                        Details »
                      </span>
                    </div>
                  </div>
                );
              })}
              {offers.length === 0 && (
                <p className="text-muted mb-0">Derzeit steht kein öffentliches Angebot bereit.</p>
              )}
            </div>
          </div>

          {/* Call-to-Action zur Registrierung */}
          <div className="d-flex flex-wrap justify-content-between align-items-center pt-3 border-top gap-3">
            <div>
              <strong className="d-block text-dark">
                Ausgewählt: {selected ? `${selected.service.service_name} – ${selected.batch_name}` : "Bitte Tarif wählen"}
              </strong>
              <span className="text-muted small">
                Unverbindlich registrieren: Nur E-Mail-Adresse und Passwort erforderlich.
              </span>
            </div>
            <button
              type="button"
              className="myn-btn myn-btn--primary px-4"
              onClick={() => {
                const stored = {
                  trial_token: trial ? trial.trial_token : undefined,
                  batch_id: batchId,
                  billing_period: billingPeriod,
                  company_name: trial ? trial.company_name : (company ? company.place_name : undefined),
                  company_address: trial ? trial.company_address : (company ? company.place_address : undefined),
                  redeem_url: trial ? trial.redeem_url : undefined,
                };
                window.sessionStorage.setItem(TRIAL_STORAGE_KEY, JSON.stringify(stored));
                navigate("/selbst-buchen");
              }}
            >
              Jetzt registrieren &amp; buchen ➔
            </button>
          </div>
        </div>

        {/* LIVE-LISTE „GERADE ERZEUGT“ MIT SUCHFELD, KLICKBARKEIT & SCAN-ZÄHLER */}
        <div id="gerade-erzeugt" className="myn-studio__live">
          <div className="d-flex flex-wrap justify-content-between align-items-end mb-3 gap-2">
            <div>
              <h2 className="myn-h3 mb-1">Gerade erzeugt</h2>
              <p className="myn-text-muted mb-0">Jedes getestete oder gebuchte Unternehmen erscheint hier genau einmal live.</p>
            </div>
            {livePaging.total > 0 && (
              <span className="myn-text-muted small">
                {livePaging.total} {livePaging.total === 1 ? "Unternehmen" : "Unternehmen insgesamt"}
              </span>
            )}
          </div>

          {/* Suchfeld mit Werbetext */}
          <div className="myn-live-search mb-3">
            <div className="input-group input-group-lg shadow-sm border rounded overflow-hidden">
              <span className="input-group-text bg-white border-0 text-muted ps-3">🔍</span>
              <input
                type="text"
                className="form-control border-0 py-3"
                placeholder="Suchen Sie Ihr Lieblings-Unternehmen und unterstützen Sie es mit einem Klick …"
                value={liveSearchQuery}
                onChange={(e) => setLiveSearchQuery(e.target.value)}
                aria-label="Suchen Sie Ihr Lieblings-Unternehmen und unterstützen Sie es mit einem Klick"
              />
              {liveSearchQuery && (
                <button
                  type="button"
                  className="btn btn-white border-0 text-muted pe-3"
                  onClick={() => setLiveSearchQuery("")}
                  title="Suche zurücksetzen"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Liste der Unternehmen: Responsive Werbe- & Visitenkarten-Karten nach Vorbild oi.gg */}
          <ul className="myn-studio__live-grid">
            {filteredLiveEntries.map((entry, index) => {
              const { from, to } = placeholderGradient(entry.company_name);
              const initials = placeholderInitials(entry.company_name);
              const visitenkarteUrl = `/v/${entry.code_value || entry.place_id}`;
              // Abgelaufene Test-Codes bleiben gelistet, verlinken aber auf die Visitenkarte.
              const cardHref = entry.redeem_url || visitenkarteUrl;

              return (
                <li key={`${entry.place_id || entry.company_name}-${index}`} className="myn-live-card-item">
                  <a
                    href={cardHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="myn-live-card-link"
                    title={`${entry.service_name || serviceLabel} von ${entry.company_name} jetzt öffnen`}
                  >
                    {/* Cover-Bereich (Flyer oder Gradient-Platzhalter wie in oi.gg) */}
                    <div className="myn-live-card-cover position-relative overflow-hidden">
                      {entry.flyer_image_url || entry.auto_cover_url ? (
                        <img
                          src={entry.flyer_image_url || entry.auto_cover_url}
                          alt={entry.flyer_image_url ? `Flyer ${entry.company_name}` : `Bild zu ${entry.company_name}`}
                          className="myn-live-card-cover-img"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="myn-live-card-cover-placeholder d-flex flex-column align-items-center justify-content-center text-white"
                          style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}
                        >
                          <span className="fw-bold fs-2">{initials}</span>
                          <span className="small text-white-50">{entry.service_name || serviceLabel}</span>
                        </div>
                      )}

                      {/* Kennzeichnung automatischer Bilder (KI: Transparenzpflicht Art. 50 KI-VO) */}
                      {!entry.flyer_image_url && entry.auto_cover_url && (
                        <span className="myn-live-card-cover-label">
                          {entry.auto_cover_source === "ai" ? "KI-generiert" : "Website-Vorschau"}
                        </span>
                      )}

                      {/* QR-Code Miniatur-Overlay unten links – auf jeder Karte. Ohne aktiven
                          Code zeigt er absolut auf die Visitenkarte, damit er scanbar bleibt. */}
                      <div className="myn-live-card-qr-badge" title="QR-Code mit MYN-Wasserzeichen">
                        <WatermarkedQr value={entry.redeem_url || `${window.location.origin}${visitenkarteUrl}`} size={50} />
                      </div>

                      {/* Visitenkarten-Button (Vorbild oi.gg): öffnet die Visitenkarte */}
                      <button
                        type="button"
                        className="myn-live-card-presentation-btn"
                        title="Visitenkarte & Flyer ansehen"
                        aria-label={`Visitenkarte und Flyer von ${entry.company_name} ansehen`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(visitenkarteUrl, "_blank", "noopener,noreferrer");
                        }}
                      >
                        <span aria-hidden="true">🪪</span>
                      </button>
                    </div>

                    {/* Karteninhalt */}
                    <div className="myn-live-card-body p-3">
                      <div className="d-flex align-items-center justify-content-between gap-1 mb-1">
                        <span className="badge bg-primary-subtle text-primary small text-truncate" style={{ maxWidth: "60%" }}>
                          {entry.service_name || serviceLabel}
                        </span>
                        {entry.has_promo && (
                          <span className="badge bg-success-subtle text-success small">
                            ★ Werbeplatz
                          </span>
                        )}
                      </div>

                      <strong className="myn-live-card-title d-block text-dark fw-bold text-truncate mb-1" title={entry.company_name}>
                        {entry.company_name}
                      </strong>

                      <span className="myn-live-card-address d-block text-muted small text-truncate mb-2" title={entry.company_address}>
                        📍 {entry.company_address}
                      </span>

                      {entry.description && (
                        <p className="myn-live-card-desc small text-secondary mb-2" title={entry.description}>
                          {entry.description}
                        </p>
                      )}

                      {/* Fußzeile mit Klick/Scan-Zähler (Augen-Icon) und Direktbewerten-CTA */}
                      <div className="d-flex align-items-center justify-content-between pt-2 mt-auto border-top">
                        <span className="badge bg-light text-dark border small d-inline-flex align-items-center gap-1" title="Anzahl der Klicks & Scans">
                          <span aria-hidden="true">👁️</span> {entry.scan_count || 1} Aufrufe
                        </span>
                        <span className="small text-primary fw-semibold d-inline-flex align-items-center gap-1">
                          {entry.redeem_url ? "Bewerten ➔" : "Ansehen ➔"}
                        </span>
                      </div>
                    </div>
                  </a>
                </li>
              );
            })}
            {filteredLiveEntries.length === 0 && (
              <li className="myn-studio__live-empty p-4 text-center text-muted bg-white rounded border w-100" style={{ gridColumn: "1 / -1" }}>
                {liveSearchQuery
                  ? `Kein Unternehmen für „${liveSearchQuery}“ gefunden.`
                  : "Noch keine Einträge vorhanden."}
              </li>
            )}
          </ul>

          {livePaging.pages > 1 && (
            <nav className="myn-pager" aria-label="Seiten der Liste „Gerade erzeugt“">
              <button
                type="button"
                className="myn-pager__btn"
                onClick={() => goToLivePage(livePaging.page - 1)}
                disabled={livePaging.page <= 1}
                aria-label="Vorherige Seite"
              >
                ‹ <span className="myn-pager__label">Zurück</span>
              </button>
              {pagerItems(livePaging.page, livePaging.pages).map((item, i) =>
                item === "gap" ? (
                  <span key={`gap-${i}`} className="myn-pager__gap" aria-hidden="true">…</span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={`myn-pager__btn${item === livePaging.page ? " is-active" : ""}`}
                    onClick={() => goToLivePage(item)}
                    aria-current={item === livePaging.page ? "page" : undefined}
                    aria-label={`Seite ${item}`}
                  >
                    {item}
                  </button>
                )
              )}
              <button
                type="button"
                className="myn-pager__btn"
                onClick={() => goToLivePage(livePaging.page + 1)}
                disabled={livePaging.page >= livePaging.pages}
                aria-label="Nächste Seite"
              >
                <span className="myn-pager__label">Weiter</span> ›
              </button>
            </nav>
          )}
        </div>
      </div>

      {/* Ausführlicher Tarif-Vergleichsmodal */}
      <TariffComparisonModal
        show={showCompareModal}
        onHide={() => setShowCompareModal(false)}
        offers={offers}
        selectedBatchId={batchId}
        billingPeriod={billingPeriod}
        setBillingPeriod={setBillingPeriod}
        onSelectOffer={handleSelectOfferFromModal}
      />
    </section>
  );
};

export default HomeQrStudio;
